import { Fragment } from 'react';
import {
  Alert,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { HEALTH_DECLARATION_CONDITIONS } from '../../../types/student';
import { HealthDeclarationAnswerDraft, WizardValues, isHealthDeclarationComplete } from './wizardTypes';
import { StudentSignatureSection } from '../StudentSignatureSection';
import { useApplicationFormApi } from './applicationFormApi';

/** The signature, saved through the form's data source (staff or portal). */
const DeclarationSignature = ({ studentId }: { studentId: number }) => {
  const api = useApplicationFormApi();
  return (
    <StudentSignatureSection
      adapter={{
        queryKey: ['student-signature', api.mode, studentId],
        getSignature: () => api.getSignature(studentId),
        getSignatureImage: () => api.getSignatureImage(studentId),
        saveSignature: (method, file) => api.saveSignature(studentId, method, file),
      }}
    />
  );
};

interface Step10Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
}

interface Step10DeclarationProps extends Step10Props {
  studentId: number | null;
}

const setAnswer = (
  values: WizardValues,
  setField: Step10Props['setField'],
  key: string,
  patch: Partial<HealthDeclarationAnswerDraft>,
) => {
  setField(
    'healthDeclarationAnswers',
    values.healthDeclarationAnswers.map((a) => (a.key === key ? { ...a, ...patch } : a)),
  );
};

/** Digital Health Declaration Form — a data-entry version of the original
 * paper form (see documentTemplates/healthDeclaration.template.ts on the
 * backend, generated from these same answers). Wording below is taken
 * verbatim from the uploaded reference PDF; do not paraphrase it. */
const HealthDeclarationForm = ({ values, setField }: Step10Props) => {
  const complete = isHealthDeclarationComplete(values);

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Divider />
      <Typography variant="subtitle1">Health Declaration</Typography>
      <Typography variant="body2">
        I hereby declare that I am <b>FREE</b> from the following diseases/conditions:
      </Typography>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ITEMS</TableCell>
              <TableCell align="center" width={60}>
                YES
              </TableCell>
              <TableCell align="center" width={60}>
                NO
              </TableCell>
              <TableCell>IF NO, PLEASE STATE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {HEALTH_DECLARATION_CONDITIONS.map((condition, index) => {
              const previous = HEALTH_DECLARATION_CONDITIONS[index - 1];
              const answer = values.healthDeclarationAnswers.find((a) => a.key === condition.key) ?? {
                key: condition.key,
                answer: '' as const,
                statement: '',
              };
              return (
                <Fragment key={condition.key}>
                  {condition.group && condition.group !== previous?.group && (
                    <TableRow key={`${condition.key}-group`}>
                      <TableCell colSpan={4} sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
                        {condition.group}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow key={condition.key}>
                    <TableCell sx={{ pl: condition.group ? 4 : 2 }}>{condition.label}</TableCell>
                    <TableCell align="center">
                      <Radio
                        size="small"
                        checked={answer.answer === 'YES'}
                        onChange={() => setAnswer(values, setField, condition.key, { answer: 'YES' })}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Radio
                        size="small"
                        checked={answer.answer === 'NO'}
                        onChange={() => setAnswer(values, setField, condition.key, { answer: 'NO' })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        disabled={answer.answer !== 'NO'}
                        placeholder={answer.answer === 'NO' ? 'Please state…' : ''}
                        error={answer.answer === 'NO' && answer.statement.trim() === ''}
                        value={answer.statement}
                        onChange={(e) => setAnswer(values, setField, condition.key, { statement: e.target.value })}
                      />
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        IF YOU HAVE SOUGHT CONSULTATION FOR ANY OF THE LISTED DISEASES/CONDITION, YOU ARE REQUIRED TO SUBMIT YOUR
        MEDICAL HISTORY/REPORT FROM YOUR TREATING PHYSICIAN TO IMC EDUCATION.
      </Typography>

      <Typography variant="body2" sx={{ textAlign: 'justify' }}>
        I declare that I will submit myself for compulsory Pre-Arrival and Post-Arrival Health Examination as per
        Belarussian regulations. In the event that I should be diagnosed with any condition that deems me UNSUITABLE
        for studies, I will bear the cost of leaving Belarus and will adhere to the stipulated immigration procedures.
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'justify' }}>
        I declare that in the event I should be diagnosed with any conditions that does not require my removal from
        Belarus but requires medical treatment and I choose to remain in Belarus to continue my studies, I will bear
        any and all costs relating directly or indirectly towards the medical management of my medical condition if
        it is not covered by the Medical Insurance policy extended in Belarus
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Name of Student as indicated in the Passport"
          fullWidth
          disabled
          value={[values.firstName, values.lastName].filter(Boolean).join(' ')}
          helperText="From Personal Details (Step 2)"
        />
        <TextField
          label="Student's Passport Number"
          fullWidth
          value={values.passportNumber}
          onChange={(e) => setField('passportNumber', e.target.value)}
          helperText="Same field as Personal Details (Step 2)"
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Student's Signature"
          fullWidth
          disabled
          value={values.studentSignatureName}
          helperText="Uses the Student signature entered above"
        />
        <TextField
          label="Parent's NIC / Passport Number"
          fullWidth
          value={values.guardianPassportOrNic}
          onChange={(e) => setField('guardianPassportOrNic', e.target.value)}
        />
      </Stack>
      <TextField
        label="Parent's Signature"
        fullWidth
        disabled
        value={values.guardianSignatureName}
        helperText="Uses the Guardian signature entered above (leave blank if not applicable)"
      />

      {!complete && (
        <Alert severity="warning">
          Every condition above must have Yes or No selected (with a statement for any "No"), and the Student's
          Signature and Passport Number must be completed, before this application can be finalized.
        </Alert>
      )}
    </Stack>
  );
};

export const Step10Declaration = ({ values, setField, studentId }: Step10DeclarationProps) => (
  <Stack spacing={2}>
    <Alert severity="info">
      I declare that the information provided in this application is true and complete to the best of my knowledge,
      and I understand that any false statement may result in the rejection of my application or cancellation of my
      enrolment.
    </Alert>

    <FormControlLabel
      control={
        <Checkbox
          checked={values.studentAgreementAccepted}
          onChange={(e) => setField('studentAgreementAccepted', e.target.checked)}
        />
      }
      label="I accept the student agreement and terms & conditions"
    />
    <FormControlLabel
      control={
        <Checkbox
          checked={values.privacyConsentAccepted}
          onChange={(e) => setField('privacyConsentAccepted', e.target.checked)}
        />
      }
      label="I consent to the processing of my personal data (privacy policy)"
    />
    <FormControlLabel
      control={
        <Checkbox
          checked={values.marketingConsentAccepted}
          onChange={(e) => setField('marketingConsentAccepted', e.target.checked)}
        />
      }
      label="I agree to receive marketing communications (optional)"
    />

    <Divider />
    <Typography variant="subtitle2">Digital signature</Typography>
    <Typography variant="body2" color="text.secondary">
      Type your full legal name below to sign this application electronically.
    </Typography>
    <TextField
      label="Student signature (type full name)"
      fullWidth
      value={values.studentSignatureName}
      onChange={(e) => setField('studentSignatureName', e.target.value)}
    />
    <TextField
      label="Guardian signature (type full name, if applicable)"
      fullWidth
      value={values.guardianSignatureName}
      onChange={(e) => setField('guardianSignatureName', e.target.value)}
    />

    {studentId ? (
      <DeclarationSignature studentId={studentId} />
    ) : (
      <Typography variant="body2" color="text.secondary">
        Complete Step 2 first — the signature is saved directly against the student.
      </Typography>
    )}

    <Divider />
    <FormControlLabel
      control={
        <Switch
          checked={values.healthDeclarationRequired}
          onChange={(e) => setField('healthDeclarationRequired', e.target.checked)}
        />
      }
      label="Health Declaration Form"
    />
    {values.healthDeclarationRequired && <HealthDeclarationForm values={values} setField={setField} />}
  </Stack>
);
